import { isExportTaskResult, LongRunningTaskStatus, type Notification, type TaskResult } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';

export interface LongRunningTask {
	/** Task ID from the backend (or locally generated for fast exports) */
	taskId: string;
	/** Current status */
	status: LongRunningTaskStatus;
	/** Timestamp when the task was initiated */
	startedAt: number;
	/** Task result (populated when the task reaches a terminal state) */
	result?: TaskResult;
	/** Notifications from the server (export warnings/errors or duplicate-task entries) */
	notifications?: Notification[];
	/** Error message (populated when failed) */
	error?: string;
	/** Department ID this task belongs to */
	departmentId: string;
	/** Polling timeout handle */
	pollHandle?: ReturnType<typeof setTimeout>;
}

export interface LongRunningExport extends LongRunningTask {
	/** Export format display name, e.g. 'Excel', 'KML', 'JSON' */
	exportType: string;
	/** Job/collection name, e.g. 'Pipeline Survey A' or '3 collections' */
	jobName: string;
	/** Combined label for backward compatibility: `${exportType} - ${jobName}` */
	label: string;
	/** Pre-fetched blob URL for instant download (set after file is fetched into memory) */
	blobUrl?: string;
	/** Whether the user has interacted with this task after it reached a terminal state */
	seen: boolean;
	/**
	 * Whether the user has been notified of this task's current state (i.e. the popover was opened since
	 * this task was added or transitioned to a terminal state). When false, the task contributes to the
	 * number badge count.
	 */
	notified: boolean;
	/**
	 * Whether the user has clicked/interacted with this completed task (downloaded the file, viewed
	 * notifications). When false on a terminal task, the red dot indicator is shown.
	 */
	actioned: boolean;
	/** Whether the user has downloaded this task's file at least once */
	downloaded: boolean;
	/** When true, suppress toast notifications for this task (used in legacy export mode) */
	silent?: boolean;
}

/** The visual status shown in the download manager UI */
export type LongRunningTaskDisplayStatus = 'processing' | 'ready' | 'downloaded' | 'warning' | 'error' | 'failed';

/** Derives the display status for a task based on its backend status */
export function getDisplayStatus(task: LongRunningExport): LongRunningTaskDisplayStatus {
	if (task.status === LongRunningTaskStatus.RUNNING) {
		return 'processing';
	}
	if (task.status === LongRunningTaskStatus.FAILED) {
		return 'failed';
	}
	if (task.status === LongRunningTaskStatus.COMPLETED_ERRORS) {
		return 'error';
	}
	const exportResult = task.result != null && isExportTaskResult(task.result) ? task.result : undefined;
	if (task.status === LongRunningTaskStatus.COMPLETED_WARNINGS) {
		if (exportResult?.url && !task.blobUrl) return 'processing';
		return 'warning';
	}
	// COMPLETED — wait for result and blob before showing final status
	if (!exportResult?.url || !task.blobUrl) {
		return 'processing';
	}
	return task.downloaded ? 'downloaded' : 'ready';
}

export class LongRunningTaskManagerState {
	@observable tasks: LongRunningTask[] = [];
	@observable isPulsing = false;
	@observable isPopoverOpen = false;
	/** Number of exports that have been initiated but not yet registered as tasks (covers the network gap) */
	@observable pendingExports = 0;

	constructor() {
		makeObservable(this);
	}

	@computed get activeTasks(): LongRunningTask[] {
		return this.tasks.filter((t) => t.status === LongRunningTaskStatus.RUNNING);
	}

	@computed get activeCount(): number {
		return this.activeTasks.length;
	}

	@computed get hasActiveTasks(): boolean {
		return this.activeCount > 0;
	}

	@computed get hasUnseenTasks(): boolean {
		return this.tasks.some((t) => LongRunningTaskManagerState.isExportTask(t) && !t.seen);
	}

	/** Count of tasks that the user has not yet been notified about (newly added or newly completed) */
	unNotifiedCountForDepartment(departmentId: string): number {
		return this.tasksForDepartment(departmentId).filter(
			(t) => LongRunningTaskManagerState.isExportTask(t) && !t.notified
		).length;
	}

	/** Whether any task in the department has not been interacted with (includes running tasks) */
	hasUnactionedForDepartment(departmentId: string): boolean {
		return this.tasksForDepartment(departmentId).some(
			(t) => LongRunningTaskManagerState.isExportTask(t) && !t.actioned
		);
	}

	/** Mark all tasks as notified (called when the popover is opened) */
	@action markAllNotified(): void {
		for (const task of this.tasks) {
			if (LongRunningTaskManagerState.isExportTask(task)) task.notified = true;
		}
	}

	/**
	 * Returns tasks filtered to the given department.
	 * The download manager only shows tasks for the current department.
	 */
	tasksForDepartment(departmentId: string): LongRunningTask[] {
		return this.tasks.filter((t) => t.departmentId === departmentId);
	}

	static isExportTask(task: LongRunningTask): task is LongRunningExport {
		const potentialExportTask = task as LongRunningExport;
		return potentialExportTask.exportType !== undefined;
	}

	@action addExportTask(
		task: Omit<LongRunningExport, 'pollHandle' | 'label' | 'seen' | 'notified' | 'actioned' | 'downloaded'>
	): void {
		const existing = this.tasks.find((t) => t.taskId === task.taskId);
		if (existing != null) {
			Object.assign(existing, task, { label: `${task.exportType} - ${task.jobName}`, startedAt: existing.startedAt });
			return;
		}
		const newExportTask: LongRunningExport = {
			...task,
			label: `${task.exportType} - ${task.jobName}`,
			seen: true,
			notified: false,
			actioned: false,
			downloaded: false,
		};
		this.tasks.push(newExportTask);
	}

	@action addTask(task: Omit<LongRunningTask, 'pollHandle'>): void {
		const existing = this.tasks.find((t) => t.taskId === task.taskId);
		if (existing != null) {
			Object.assign(existing, task, { startedAt: existing.startedAt });
			return;
		}
		this.tasks.push({ ...task });
	}

	@action updateTask(taskId: string, updates: Partial<LongRunningTask>): void {
		const task = this.tasks.find((t) => t.taskId === taskId);
		if (task != null) {
			Object.assign(task, updates);
		}
	}

	/** Whether there are any non-running tasks that can be cleared */
	hasClearableItemsForDepartment(departmentId: string): boolean {
		return this.tasksForDepartment(departmentId).some((t) => t.status !== LongRunningTaskStatus.RUNNING);
	}

	/** Remove all non-running tasks for the given department. Revokes blob URLs and clears poll handles. */
	@action clearCompleted(departmentId: string): void {
		const exportTasks = this.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const toRemove = exportTasks.filter(
			(t) => t.departmentId === departmentId && t.status !== LongRunningTaskStatus.RUNNING
		);
		for (const task of toRemove) {
			if (task.pollHandle) clearTimeout(task.pollHandle);
			if (task.blobUrl) URL.revokeObjectURL(task.blobUrl);
		}
		const toRemoveIds = new Set(toRemove.map((t) => t.taskId));
		this.tasks = this.tasks.filter((t) => !toRemoveIds.has(t.taskId));
	}

	/** Stop all polling timeouts and revoke blob URLs. Call on teardown. */
	@action dispose(): void {
		for (const task of this.tasks) {
			if (task.pollHandle) {
				clearTimeout(task.pollHandle);
			}
			if (LongRunningTaskManagerState.isExportTask(task) && task.blobUrl) {
				URL.revokeObjectURL(task.blobUrl);
			}
		}
	}
}
